import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/Header'
import { useAlert } from '../contexts/AlertContext'
import { useAuth } from '../hooks/useAuth'
import {
  attendanceCreatePunch,
  attendanceGetAccess,
  attendanceLoadEventTypes,
  attendanceLoadMyLeaveBalances,
  attendanceLoadMyPrehled,
  attendanceLoadMyPunches,
  downloadAttendancePrehledPdf,
  type AttendanceEventType,
  type AttendanceLeaderPrehled,
  type AttendanceLeaveBalance,
  type AttendanceLeaveKind,
  type AttendancePrehledDay,
  type AttendancePunch,
  type AttendancePunchType,
} from '../lib/apiPhpAdapter'
import './AttendanceUserView.css'

function formatPunchClock(iso: string): string {
  const m = String(iso).match(/[ T](\d{2}):(\d{2})(?::(\d{2}))?/)
  return m ? `${m[1]}:${m[2]}${m[3] ? `:${m[3]}` : ''}` : iso
}

function formatPunchTime(iso: string): string {
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/)
  if (m) {
    const sec = m[6] ?? '00'
    return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}:${sec}`
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function punchDayNumber(iso: string): number | null {
  const m = String(iso).match(/^\d{4}-\d{2}-(\d{2})/)
  return m ? parseInt(m[1], 10) : null
}

function punchYmd(iso: string): string | null {
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : null
}

/** Dnešní datum v Europe/Prague (YYYY-MM-DD), shodné se serverem. */
function pragueTodayYmd(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Prague',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

function punchEventKind(
  code: string,
  catalog: AttendanceEventType[],
): 'in' | 'out' {
  const found = catalog.find((e) => e.code === code)
  if (found?.kind === 'in' || found?.kind === 'out') return found.kind
  if (code.startsWith('departure') || code.endsWith('_out')) return 'out'
  return 'in'
}

function monthTitle(year: number, month: number): string {
  return `${String(month).padStart(2, '0')}/${year}`
}

function dayPlanLabel(day: AttendancePrehledDay): string {
  if (day.absence?.label) return day.absence.label
  if (day.absence?.code) return day.absence.code
  if (day.shift?.label) return day.shift.label
  if (day.shift?.name) return day.shift.name.slice(0, 4)
  return ''
}

export default function AttendanceUserView() {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { showAlert } = useAlert()
  const [gateOk, setGateOk] = useState<boolean | null>(null)
  const [busyType, setBusyType] = useState<AttendancePunchType | null>(null)
  const [eventTypes, setEventTypes] = useState<AttendanceEventType[]>([])
  const [eventTypesLoading, setEventTypesLoading] = useState(false)
  /** Dnešní punchy (pro střídání příchod ↔ odchod). */
  const [todayPunches, setTodayPunches] = useState<AttendancePunch[]>([])

  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [prehled, setPrehled] = useState<AttendanceLeaderPrehled | null>(null)
  const [balances, setBalances] = useState<Record<AttendanceLeaveKind, AttendanceLeaveBalance> | null>(
    null,
  )
  const [loadingPrehled, setLoadingPrehled] = useState(false)
  const [pdfBusy, setPdfBusy] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [dayModal, setDayModal] = useState<AttendancePrehledDay | null>(null)

  const shiftMonth = (delta: number) => {
    let m = month + delta
    let y = year
    if (m < 1) {
      m = 12
      y -= 1
    } else if (m > 12) {
      m = 1
      y += 1
    }
    setMonth(m)
    setYear(y)
    setDayModal(null)
  }

  const reloadTodayGate = useCallback(async () => {
    try {
      const list = await attendanceLoadMyPunches(40)
      const ymd = pragueTodayYmd()
      const today = list
        .filter((p) => punchYmd(p.punched_at) === ymd)
        .slice()
        .sort((a, b) => String(a.punched_at).localeCompare(String(b.punched_at)))
      setTodayPunches(today)
    } catch {
      setTodayPunches([])
    }
  }, [])

  const reloadPrehled = useCallback(async () => {
    setLoadingPrehled(true)
    setLinkError(null)
    try {
      const [p, b] = await Promise.all([
        attendanceLoadMyPrehled(year, month),
        attendanceLoadMyLeaveBalances(year),
      ])
      setPrehled(p)
      setBalances(b.balances)
      setDayModal((prev) => {
        if (!prev) return null
        return p.days.find((d) => d.day_date === prev.day_date) ?? null
      })
      await reloadTodayGate()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Nepodařilo se načíst přehled'
      if (/not_linked|propojen/i.test(msg)) {
        setLinkError(msg)
        setPrehled(null)
        setBalances(null)
        setTodayPunches([])
      } else {
        showAlert(msg, 'error')
      }
    } finally {
      setLoadingPrehled(false)
    }
  }, [year, month, showAlert, reloadTodayGate])

  useEffect(() => {
    if (authLoading || !user) return
    let cancelled = false
    attendanceGetAccess()
      .then((a) => {
        if (cancelled) return
        if (!a.canUser) {
          setGateOk(false)
          navigate('/attendance', { replace: true })
          return
        }
        setGateOk(true)
      })
      .catch(() => {
        if (!cancelled) navigate('/attendance', { replace: true })
      })
    return () => {
      cancelled = true
    }
  }, [authLoading, user, navigate])

  useEffect(() => {
    if (!gateOk) return
    void reloadPrehled()
  }, [gateOk, reloadPrehled])

  useEffect(() => {
    if (!gateOk) return
    let cancelled = false
    setEventTypesLoading(true)
    attendanceLoadEventTypes({ activeOnly: true })
      .then((list) => {
        if (!cancelled) setEventTypes(Array.isArray(list) ? list : [])
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst události', 'error')
          setEventTypes([])
        }
      })
      .finally(() => {
        if (!cancelled) setEventTypesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [gateOk, showAlert])

  useEffect(() => {
    if (!dayModal) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDayModal(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dayModal])

  const dayPunches: AttendancePunch[] = useMemo(() => {
    if (!prehled || !dayModal) return []
    return prehled.punches
      .filter((p) => punchDayNumber(p.punched_at) === dayModal.day)
      .slice()
      .sort((a, b) => String(a.punched_at).localeCompare(String(b.punched_at)))
  }, [prehled, dayModal])

  /** Další povolený směr: bez aktivního příchodu → in, po příchodu → out. */
  const expectedKind = useMemo((): 'in' | 'out' => {
    if (todayPunches.length === 0) return 'in'
    const last = todayPunches[todayPunches.length - 1]
    return punchEventKind(last.punch_type, eventTypes) === 'in' ? 'out' : 'in'
  }, [todayPunches, eventTypes])

  const onPunch = async (type: AttendancePunchType) => {
    if (busyType) return
    const kind = punchEventKind(type, eventTypes)
    if (kind !== expectedKind) {
      showAlert(
        expectedKind === 'in'
          ? 'Nejdříve zaregistrujte příchod.'
          : 'Nejdříve zaregistrujte odchod (máte aktivní příchod).',
        'warning',
      )
      return
    }
    setBusyType(type)
    try {
      const created = await attendanceCreatePunch(type)
      showAlert(
        `${created.punch_type_label} · ${formatPunchTime(created.punched_at)} · ${created.building_code}`,
        'success',
      )
      await reloadPrehled()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Záznam se nepodařilo uložit', 'error')
    } finally {
      setBusyType(null)
    }
  }

  if (authLoading || !user || gateOk === null) {
    return (
      <div className="app">
        <Header title="Docházka — Uživatel" showLogo />
        <div style={{ padding: '2rem', color: '#fff' }}>Načítání…</div>
      </div>
    )
  }

  return (
    <div className="app">
      <Header
        leftButtons={
          <button type="button" className="back-button" onClick={() => navigate('/attendance')}>
            ← Zpět na výběr role
          </button>
        }
        title="Docházka — Uživatel"
        rightContent={
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>{user.name}</span>
        }
        showLogo
      />

      <div className="att-user">
        <section className="att-user__card">
          <h2>Registrace docházky</h2>
          <p className="att-user__hint">
            Pro správné přiřazení místa doporučujeme být při záznamu připojeni ke školní síti. Práce
            z domova, na nepřímé činnosti nebo z vlastního připojení je v pořádku — v takovém případě
            se místo označí jako mimo školní budovy (X). Aktivní jsou jen tlačítka podle posledního
            záznamu dnes (příchod ↔ odchod).
          </p>

          <div className="att-user__actions">
            {eventTypesLoading && eventTypes.length === 0 ? (
              <p className="att-user__muted" style={{ gridColumn: '1 / -1', margin: 0 }}>
                Načítání událostí…
              </p>
            ) : null}
            {!eventTypesLoading && eventTypes.length === 0 ? (
              <p className="att-user__muted" style={{ gridColumn: '1 / -1', margin: 0 }}>
                Žádné aktivní události. Správce je zapne v nastavení docházky (Události).
              </p>
            ) : null}
            {eventTypes.map((a) => {
              const kind = a.kind === 'out' ? 'out' : 'in'
              const allowed = kind === expectedKind
              const busy = busyType !== null
              const customColor =
                a.color && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(a.color) ? a.color : null
              return (
                <button
                  key={a.code}
                  type="button"
                  className={[
                    'att-user__btn',
                    `att-user__btn--${kind}`,
                    !allowed ? 'is-inactive' : '',
                    busyType === a.code ? 'is-busy' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={customColor && allowed && !busy ? { background: customColor } : undefined}
                  disabled={busy || !allowed}
                  title={
                    allowed
                      ? undefined
                      : expectedKind === 'in'
                        ? 'Nejdříve zaregistrujte příchod'
                        : 'Nejdříve zaregistrujte odchod'
                  }
                  onClick={() => void onPunch(a.code)}
                >
                  {busyType === a.code ? 'Ukládám…' : a.name}
                </button>
              )
            })}
          </div>
        </section>

        <section className="att-user__card">
          <div className="att-user__month-nav">
            <h2 style={{ margin: 0 }}>Můj měsíční přehled</h2>
            <div className="att-user__month-controls">
              <button type="button" disabled={loadingPrehled} onClick={() => shiftMonth(-1)}>
                ‹
              </button>
              <strong>{monthTitle(year, month)}</strong>
              <button type="button" disabled={loadingPrehled} onClick={() => shiftMonth(1)}>
                ›
              </button>
              {prehled?.closed ? (
                <button
                  type="button"
                  className="att-user__pdf"
                  disabled={pdfBusy || !prehled}
                  onClick={() => {
                    if (!prehled) return
                    void (async () => {
                      setPdfBusy(true)
                      try {
                        await downloadAttendancePrehledPdf(prehled.employee_id, year, month)
                      } catch (e: unknown) {
                        showAlert(e instanceof Error ? e.message : 'PDF se nepodařilo stáhnout', 'error')
                      } finally {
                        setPdfBusy(false)
                      }
                    })()
                  }}
                >
                  {pdfBusy ? 'PDF…' : 'Stáhnout PDF'}
                </button>
              ) : null}
            </div>
          </div>

          <p className="att-user__hint" style={{ marginBottom: '0.75rem' }}>
            Kliknutím na den zobrazíte záznamy (události) daného dne.
          </p>

          {linkError ? <p className="att-user__warn">{linkError}</p> : null}
          {loadingPrehled && !prehled ? <p className="att-user__muted">Načítání přehledu…</p> : null}

          {balances ? (
            <div className="att-user__balances">
              <div>
                <span>Dovolená</span>
                <strong>
                  {balances.dovolena.remaining} / {balances.dovolena.entitled} h
                </strong>
                <em>vyčerpáno {balances.dovolena.used} h</em>
              </div>
              <div>
                <span>Samostudium</span>
                <strong>
                  {balances.samostudium.remaining} / {balances.samostudium.entitled} dní
                </strong>
                <em>vyčerpáno {balances.samostudium.used} d</em>
              </div>
              {prehled?.banka ? (
                <div>
                  <span>Banka hodin</span>
                  <strong>{prehled.banka.closing_label}</strong>
                  <em>{prehled.banka.posted ? 'po uzávěrce' : 'odhad'}</em>
                </div>
              ) : null}
              {prehled?.soucet.stravenky_count != null ? (
                <div>
                  <span>Stravenky</span>
                  <strong>{prehled.soucet.stravenky_count}×</strong>
                  <em>za měsíc</em>
                </div>
              ) : null}
            </div>
          ) : null}

          {prehled ? (
            <>
              <div className="att-user__soucet">
                <span>Odpracováno {prehled.soucet.odpracovano_label}</span>
                <span>Norma {prehled.soucet.norma_label}</span>
                <span>Saldo {prehled.soucet.saldo_label}</span>
                <span>{prehled.closed ? 'Uzavřeno' : 'Neuzavřeno'}</span>
              </div>
              <div className="att-user__days-wrap">
                <table className="att-user__days">
                  <thead>
                    <tr>
                      <th>Den</th>
                      <th>Kód</th>
                      <th>Odpracováno</th>
                      <th>Norma</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prehled.days.map((day) => (
                      <tr
                        key={day.day_date}
                        className={[
                          'att-user__day-row',
                          day.weekend ? 'is-weekend' : '',
                          day.absence ? 'is-absence' : '',
                          day.open ? 'is-open' : '',
                          dayModal?.day_date === day.day_date ? 'is-selected' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setDayModal(day)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setDayModal(day)
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        title="Zobrazit záznamy dne"
                      >
                        <td>
                          {day.day}. {day.weekday_short}
                        </td>
                        <td>{dayPlanLabel(day) || (day.weekend ? '' : '·')}</td>
                        <td>
                          {day.hours_label}
                          {day.open ? '…' : ''}
                        </td>
                        <td>{day.norm_hours_label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </section>
      </div>

      {dayModal ? (
        <div
          className="att-user__modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="att-user-day-title"
          onClick={() => setDayModal(null)}
        >
          <div className="att-user__modal" onClick={(e) => e.stopPropagation()}>
            <div className="att-user__modal-head">
              <h3 id="att-user-day-title">
                {dayModal.day}. {dayModal.weekday_short} · {dayModal.day_date.split('-').reverse().join('.')}
              </h3>
              <button type="button" className="att-user__modal-close" onClick={() => setDayModal(null)}>
                Zavřít
              </button>
            </div>
            <dl className="att-user__modal-meta">
              <div>
                <dt>Kód</dt>
                <dd>{dayPlanLabel(dayModal) || '—'}</dd>
              </div>
              <div>
                <dt>Odpracováno</dt>
                <dd>
                  {dayModal.hours_label}
                  {dayModal.open ? '…' : ''}
                </dd>
              </div>
              <div>
                <dt>Norma</dt>
                <dd>{dayModal.norm_hours_label}</dd>
              </div>
              {dayModal.note ? (
                <div>
                  <dt>Poznámka</dt>
                  <dd>{dayModal.note}</dd>
                </div>
              ) : null}
            </dl>
            <h4 className="att-user__modal-sub">Záznamy dne</h4>
            {dayPunches.length === 0 ? (
              <p className="att-user__muted">Žádné události v tento den.</p>
            ) : (
              <ul className="att-user__list">
                {dayPunches.map((p) => (
                  <li key={p.id}>
                    <div className="att-user__list-main">
                      <strong>{p.punch_type_label}</strong>
                      <span className="att-user__bldg">{p.building_code}</span>
                      {p.source === 'manual' ? (
                        <span className="att-user__src">ručně</span>
                      ) : null}
                    </div>
                    <div className="att-user__list-time">{formatPunchClock(p.punched_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
